import type RAPIER from '@dimforge/rapier3d-compat';
import * as THREE from 'three';
import type { DriveInput } from '../input/Input';
import { BOOST_ACCEL, BOOST_EXTRA_SPEED } from '../race/boost';
import { clamp } from '../util/format';
import type { SurfaceParams } from './surfaces';

const _quat = new THREE.Quaternion();
const _forward = new THREE.Vector3();
const _up = new THREE.Vector3();

export interface DriveTelemetry {
  forwardSpeed: number;
  lateralSpeed: number;
  grounded: boolean;
}

/**
 * Arcade forces on top of a Rapier rigid body.
 * Not a full vehicle simulation: grip, drift and steering are tuned for a shopping cart.
 */
export function stepArcadeVehicle(
  body: RAPIER.RigidBody,
  input: DriveInput,
  surface: SurfaceParams,
  grounded: boolean,
  dt: number,
): DriveTelemetry {
  const mass = Math.max(1, body.mass());
  const rotation = body.rotation();
  _quat.set(rotation.x, rotation.y, rotation.z, rotation.w);
  _forward.set(0, 0, 1).applyQuaternion(_quat);
  _up.set(0, 1, 0).applyQuaternion(_quat);

  const linear = body.linvel();
  const vx = linear.x;
  const vz = linear.z;

  const fwdX = _forward.x;
  const fwdZ = _forward.z;
  const fwdLen = Math.hypot(fwdX, fwdZ) || 1;
  const fx = fwdX / fwdLen;
  const fz = fwdZ / fwdLen;
  const rx = fz;
  const rz = -fx;

  const forwardSpeed = vx * fx + vz * fz;
  const lateralSpeed = vx * rx + vz * rz;
  const speedAbs = Math.abs(forwardSpeed);

  let impulseX = 0;
  let impulseZ = 0;

  const speedT = clamp((speedAbs - 3) / 12, 0, 1);
  const steerT = Math.min(1, Math.abs(input.steer));
  // On throttle, grip stays close to the surface value so a lap is finishable.
  // Releasing the throttle or pulling the handbrake is what makes the cart slide.
  let gripScale = 1;
  if (input.throttle < 0.15 && speedAbs > 7) gripScale *= THREE.MathUtils.lerp(1, 0.5, speedT);
  if (steerT > 0.35 && speedT > 0.35 && input.throttle < 0.85) gripScale *= 0.72;
  if (input.handbrake) gripScale *= 0.22;
  const gripRate = surface.grip * gripScale;
  const boosting = input.boost && grounded;
  const maxSpeed = surface.maxSpeed + (boosting ? BOOST_EXTRA_SPEED : 0);
  const steerSense = THREE.MathUtils.lerp(1, 0.36, clamp(speedAbs / Math.max(1, surface.maxSpeed), 0, 1)) * surface.steer;

  if (grounded) {
    const wantsDrive = (input.throttle > 0.01 && input.brake < 0.35) || boosting;
    if (wantsDrive && forwardSpeed < maxSpeed) {
      const falloff = 1 - clamp(Math.max(0, forwardSpeed) / maxSpeed, 0, 1) * 0.4;
      const accel = input.throttle * surface.accel * falloff + (boosting ? BOOST_ACCEL : 0);
      impulseX += fx * accel * mass * dt;
      impulseZ += fz * accel * mass * dt;
    }

    if (input.brake > 0.05 && input.throttle < 0.2 && forwardSpeed < 0.45) {
      const reverseMax = 4.6;
      if (forwardSpeed > -reverseMax) {
        const accel = input.brake * 4.8;
        impulseX -= fx * accel * mass * dt;
        impulseZ -= fz * accel * mass * dt;
      }
    } else if (input.brake > 0.01 && speedAbs > 0.08) {
      const brakeAccel = input.brake * surface.brake * (input.handbrake ? 1.25 : 1);
      const decel = Math.min(speedAbs / dt, brakeAccel);
      const sign = Math.sign(forwardSpeed);
      impulseX -= fx * sign * decel * mass * dt;
      impulseZ -= fz * sign * decel * mass * dt;
    }

    const horiz = Math.hypot(vx, vz);
    if (horiz > 0.08) {
      const roll = Math.min(horiz / dt, surface.rolling);
      impulseX -= (vx / horiz) * roll * mass * dt;
      impulseZ -= (vz / horiz) * roll * mass * dt;
    }

    const cancel = 1 - Math.exp(-gripRate * dt);
    impulseX -= rx * lateralSpeed * cancel * mass;
    impulseZ -= rz * lateralSpeed * cancel * mass;

    if (forwardSpeed > maxSpeed) {
      const excess = forwardSpeed - maxSpeed;
      impulseX -= fx * excess * mass;
      impulseZ -= fz * excess * mass;
    }
    if (forwardSpeed < -5) {
      const excess = forwardSpeed + 5;
      impulseX -= fx * excess * mass;
      impulseZ -= fz * excess * mass;
    }
  }

  if (impulseX !== 0 || impulseZ !== 0) {
    body.applyImpulse({ x: impulseX, y: 0, z: impulseZ }, true);
  }

  const angular = body.angvel();
  const radius = 3.3 + speedAbs * 0.48;
  let targetYaw = input.steer * steerSense * ((0.4 + speedAbs) / radius);
  if (forwardSpeed < -0.4) targetYaw *= -1;
  if (input.handbrake && speedAbs > 3.5) targetYaw += input.steer * 1.55 * steerSense;
  if (!grounded) targetYaw *= 0.2;
  const yaw = angular.y + (targetYaw - angular.y) * (1 - Math.exp(-8 * dt));

  const upright = grounded ? 7.5 : 3.5;
  const angX = angular.x * Math.exp(-3.5 * dt) - _up.z * upright;
  const angZ = angular.z * Math.exp(-3.5 * dt) + _up.x * upright;
  body.setAngvel({ x: angX, y: yaw, z: angZ }, true);

  return { forwardSpeed, lateralSpeed, grounded };
}
